import { AgGridState } from '@northfork/ag-grid-angular'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { LayoutConfig } from 'golden-layout'

export const workspaceFeatureKey = 'workspace'

export interface StackToPanelKey {
  stackId: string
  panelKey: string
}

export interface WorkspaceSummary {
  workspaceId: string //workspaceId this is used in the prefix
  workspacePrefix: string //example: workspace/1/__BASE32__<userid@realm>/<uuid> <- directory
  name: string
  description: string
}

export interface Workspace extends WorkspaceSummary {
  workspace: ExtendedLayoutConfig
}

export interface ExtendedLayoutConfig extends LayoutConfig {
  modalPositions?: { [key: string]: { top: string; left: string } }
}

export interface SavedView {
  savedViewId: string //savedViewId this is used in the prefix
  savedViewPrefix: string
  gridKey: string //Unique identifier set in component that uses ag-grid gridKey =`${PanelKey}-${GridName}`
  savedView: AgGridState
}

export type WorkspaceRedux = {
  activeWorkspace?: Workspace
  activeSavedViews?: SavedView[]
  updatedSavedView?: SavedView
  activeTabsPerStack: Record<string, string> //stackId to panelKey
  usingLocalStorage: boolean
  workspacesLoaded: boolean
}

export const workspaceInitialState: WorkspaceRedux = {
  activeWorkspace: undefined,
  activeSavedViews: undefined,
  activeTabsPerStack: {},
  usingLocalStorage: false,
  workspacesLoaded: false,
}

/**
 * The GoldenLayout object LayoutConfig causes type errors when spread. Hence all of the unusual type casting below.
 */
export const workspaceSlice = createSlice({
  name: workspaceFeatureKey,
  initialState: workspaceInitialState,
  reducers: {
    updateModalPositions: (
      oldState,
      action: PayloadAction<{ [key: string]: { top: string; left: string } }>,
    ) => {
      if (!oldState.activeWorkspace) {
        return oldState
      }
      return {
        ...oldState,
        activeWorkspace: {
          ...oldState.activeWorkspace,
          workspace: {
            ...oldState.activeWorkspace.workspace,
            modalPositions: action.payload,
          },
        },
      }
    },
    setActiveWorkspace: (oldState, action: PayloadAction<Workspace>) => {
      const { activeWorkspace, ...restOfState } = oldState
      return {
        ...restOfState,
        activeWorkspace: action.payload,
        activeSavedViews: undefined,
      }
    },
    setActiveSavedViews: (
      oldState,
      action: PayloadAction<SavedView[] | undefined>,
    ) => {
      const { activeWorkspace, ...restOfState } = oldState
      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        activeSavedViews: action.payload,
      }
    },
    updateActiveLayout: (oldState, action: PayloadAction<LayoutConfig>) => {
      const { activeWorkspace, ...restOfState } = oldState

      // Preserve the existing modalPositions
      const modalPositions = activeWorkspace?.workspace?.modalPositions ?? {}

      const updatedWorkspace = {
        ...activeWorkspace,
        workspace: {
          ...action.payload,
          modalPositions: modalPositions,
        } as ExtendedLayoutConfig,
      }

      const newState = {
        ...restOfState,
        activeWorkspace: updatedWorkspace as unknown as Workspace,
      }
      return newState
    },
    updateSavedView: (oldState, action: PayloadAction<SavedView>) => {
      const { activeWorkspace, activeSavedViews, ...restOfState } = oldState
      const updatedSavedView = action.payload

      if (
        updatedSavedView.savedView.columnState == null ||
        updatedSavedView.savedView.columnState.length === 0
      ) {
        console.error(
          `Ignored update with empty column state to saved view with prefix: ${action.payload.savedViewPrefix}.`,
        )
        return {
          ...restOfState,
          activeSavedViews,
          activeWorkspace: activeWorkspace as unknown as Workspace,
        }
      }

      const updatedSavedViews = [
        updatedSavedView,
        ...(activeSavedViews ?? []).filter(
          view => view.savedViewPrefix !== updatedSavedView.savedViewPrefix,
        ),
      ]

      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        activeSavedViews: updatedSavedViews,
        updatedSavedView: updatedSavedView,
      }
    },
    destroyStack: (oldState, action: PayloadAction<string>) => {
      const { activeWorkspace, ...restOfState } = oldState
      const activeTabsPerStack: Record<string, string> = Object.fromEntries(
        Object.entries(oldState.activeTabsPerStack).filter(
          x => x[0] !== action.payload,
        ),
      )
      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        activeTabsPerStack,
      }
    },
    updateStack: (oldState, action: PayloadAction<StackToPanelKey>) => {
      const { activeWorkspace, ...restOfState } = oldState
      const activeTabsPerStack: Record<string, string> = {
        ...oldState.activeTabsPerStack,
        [action.payload.stackId]: action.payload.panelKey,
      }
      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        activeTabsPerStack,
      }
    },
    setUsingLocalStorage: oldState => {
      const { activeWorkspace, ...restOfState } = oldState
      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        usingLocalStorage: true,
      }
    },
    setWorkspacesLoaded: oldState => {
      const { activeWorkspace, ...restOfState } = oldState
      return {
        ...restOfState,
        activeWorkspace: activeWorkspace as unknown as Workspace,
        workspacesLoaded: true,
      }
    },
  },
})

export const {
  updateActiveLayout,
  updateModalPositions,
  updateSavedView,
  setActiveSavedViews,
  setActiveWorkspace,
  destroyStack,
  updateStack,
  setUsingLocalStorage,
  setWorkspacesLoaded,
} = workspaceSlice.actions

export default workspaceSlice.reducer
